/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useState } from 'react';

// Mantine
import { Box, Highlight } from '@mantine/core';

// Styles
import classes from './Step3.module.css';

// Components
import { Datatable } from '@components/organisms';
import { Alert, Pagination } from '@components/atoms';

// Datatable config
import { tableSchemaBase as columns } from './step3-productions.columns';

// Store
import useStore from '@store/index';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { TSortColumn } from '@global-types/index';

// Hooks
import { usePagination } from '@hooks/usePagination';
import { DrawerProduction } from '@features/sourcing/productions/components/DrawerProduction';

import sum from 'lodash/sum';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';

import isNumber from 'lodash/isNumber';
import { formatNumeric } from '@utils/formats';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Step3 = () => {
	/* ********************************** Hooks ********************************* */
	const [openDrawer, setOpenDrawer] = useState(false);

	const {
		productionStartMintsSelectedRecords,
		setProductionsStartMintsSelectedRecords,
		productionStartMintsSortStatus,
		setProductionsStartMintsSortStatus,
		setProductionsStartMintsSortParams,
		setProductionsStartMintsSelectedRow,
		productionStartMintsSelectedRow,
	} = useStore();

	const {
		totalPages,
		pageSize,
		handlePageSizeChange,
		total,
		currentItems,
		currentPage,
		handlePageChange,
	} = usePagination(productionStartMintsSelectedRecords);
	/* ********************************** Helpers ********************************* */

	const handleSortParams = (_column: any) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'production':
				sortParams = 'production';
				break;
			case 'mintStatus':
				sortParams = 'mintStatus';
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setProductionsStartMintsSortParams(sortParams);
		} else {
			setProductionsStartMintsSortParams(
				'-' + sortParams
			);
		}
	};
	const { t } = useTranslation();
	/* ******************************** VARIABLES ******************************* */

	const TotalTokenEquivalents = isArray(
		productionStartMintsSelectedRecords
	)
		? sum(
				productionStartMintsSelectedRecords.map(
					(item) => {
						if (isNil(item.numericTokenEquivalent)) {
							return 0;
						}
						return item.numericTokenEquivalent;
					}
				)
			)
		: 0;

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			{/*Drawer production */}
			<DrawerProduction
				opened={openDrawer}
				handleverify={() => {}}
				handledelete={() => {}}
				handlestartmint={() => {}}
				onClose={() => {
					setOpenDrawer(false);
				}}
				position={'right'}
				data={productionStartMintsSelectedRow}
				fromstepper
			/>
			<Box className={classes.content}>
				<Highlight
					highlight={[
						'highlighted',
						'' + isNumber(TotalTokenEquivalents)
							? formatNumeric(TotalTokenEquivalents)
							: '0',
					]}
					highlightStyles={{
						backgroundImage:
							'linear-gradient(45deg, #647FE8, #647FE8)',
						fontWeight: 700,
						WebkitBackgroundClip: 'text',
						WebkitTextFillColor: 'transparent',
					}}
					c="var(--mantine-color-dark)"
					fw={500}
					className={classes.title}
				>
					{productionStartMintsSelectedRecords.length +
						` ${t(
							'productions.productionsReadyToMintWithAnEquivalentOf'
						)} ${
							TotalTokenEquivalents
								? formatNumeric(TotalTokenEquivalents)
								: 0
						} tCO2e `}
				</Highlight>
				<Box className={classes.wrapperFilters}>
					<Alert
						color="alert"
						title={t(
							'productions.pleaseVerifyTheProductionsListBeforeStartingTheMint'
						)}
						variant={'light'}
					/>
				</Box>
				<Box className={classes.contentDatatable}>
					<Datatable
						loading={false}
						columns={columns}
						data={currentItems}
						onRowClick={() => {}}
						onViewMore={(row: any) => {
							setProductionsStartMintsSelectedRow(row);
							setOpenDrawer(true);
						}}
						onRemoveItem={(row: any) => {
							setProductionsStartMintsSelectedRecords(
								productionStartMintsSelectedRecords.filter(
									(item: any) => item.id != row.id
								)
							);
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						onSortStatusChange={(data: TSortColumn) => {
							handleSortParams(data);
							setProductionsStartMintsSortStatus(data);
						}}
						sortstatus={productionStartMintsSortStatus}
						selectedRecords={null}
					/>
				</Box>
				<Pagination
					pagesize={pageSize}
					totalitems={total}
					activepage={currentPage}
					handlechangesize={function (
						_size: string | number
					) {
						handlePageSizeChange(Number(_size));
					}}
					onChange={(currentPage) => {
						handlePageChange(currentPage);
					}}
					total={
						Number.isInteger(totalPages) ? totalPages : 1
					}
				/>
			</Box>
		</Box>
	);
};
